import { useState, useEffect } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  LayoutDashboard, Package, ShoppingCart, Users, Settings, LogOut,
  Search, Plus, Edit, Trash2, Eye, ChevronDown, Download, RefreshCw,
  DollarSign, TrendingUp, Clock, CheckCircle, Truck, XCircle, Menu, X,
  Store, Bell, FileText, BarChart3, Tag, Layers
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  // Auth
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  
  // Navigation
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Data
  const [stats, setStats] = useState({ orders: 0, products: 0, customers: 0, revenue: 0 });
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [storeSettings, setStoreSettings] = useState({
    store_name: "American Distributors",
    email: "info@importbaba.com",
    phone: "+1 (630)422-1915",
    address: "1049 Industrial Dr, Bensenville, IL 60106",
    nicotine_warning: "THIS PRODUCT CONTAINS NICOTINE. NICOTINE IS AN ADDICTIVE CHEMICAL.",
    shipping_policy: "Shipping Policy Update: To serve you better, we've made changes to our shipping policy.",
    sales_final_notice: "Sales Are Final | No Returns | No Exchanges"
  });
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Check admin login on mount
  useEffect(() => {
    const savedAdmin = localStorage.getItem("importbaba_admin");
    if (savedAdmin) {
      setAdminUser(JSON.parse(savedAdmin));
      setIsLoggedIn(true);
      loadData();
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load products
      const prodRes = await axios.get(`${API}/supplier-products?limit=500`);
      setProducts(prodRes.data.products || []);
      
      // Load orders from backend
      const ordersRes = await axios.get(`${API}/storefront/orders?limit=200`);
      const fetchedOrders = (ordersRes.data.orders || []).map(o => ({
        ...o,
        id: o.order_id,
        date: o.created_at,
      }));
      setOrders(fetchedOrders);
      
      // Calculate stats from backend data
      const orderStats = ordersRes.data.stats || {};
      setStats({
        orders: orderStats.total_orders || fetchedOrders.length,
        products: prodRes.data.products?.length || 0,
        customers: [...new Set(fetchedOrders.map(o => o.customer?.email).filter(Boolean))].length,
        revenue: orderStats.total_revenue || fetchedOrders.reduce((sum, o) => sum + (o.total || 0), 0)
      });

      // Load store settings
      try {
        const settingsRes = await axios.get(`${API}/store-settings`);
        if (settingsRes.data) setStoreSettings(settingsRes.data);
      } catch (e) { console.error("Error loading settings:", e); }
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  // Admin Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        username: loginForm.username,
        password: loginForm.password,
      });
      const { token, user } = res.data;
      const admin = { ...user, token };
      setAdminUser(admin);
      setIsLoggedIn(true);
      localStorage.setItem("importbaba_admin", JSON.stringify(admin));
      toast.success("Welcome to Admin Panel!");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setAdminUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem("importbaba_admin");
    toast.success("Logged out");
  };

  // Order Management
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/storefront/orders/${orderId}/status?status=${newStatus}`);
      const updated = orders.map(o => 
        o.id === orderId ? { ...o, status: newStatus, updatedAt: new Date().toISOString() } : o
      );
      setOrders(updated);
      toast.success(`Order status updated to ${newStatus}`);
    } catch (err) {
      console.error("Error updating order:", err);
      toast.error("Failed to update order status");
    }
  };

  // Sync Products
  const syncProducts = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/suppliers/sync-hydroxy`);
      toast.success("Product sync started! This may take a few minutes.");
      // Reload products after a delay
      setTimeout(loadData, 10000);
    } catch (err) {
      toast.error("Failed to start sync");
    }
    setLoading(false);
  };

  // ============================================
  // LOGIN PAGE
  // ============================================
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="bg-[#1a237e] text-white font-bold text-2xl px-3 py-2 rounded">
                <span className="text-red-500">▶</span> AD
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Admin Panel</h1>
            <p className="text-gray-500">Sign in to manage your store</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter password"
              />
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-[#1a237e] text-white rounded-lg font-semibold hover:bg-[#283593] transition-colors"
            >
              Sign In
            </button>
          </form>
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Demo Credentials:</strong><br />
              Username: admin<br />
              Password: admin123
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // SIDEBAR
  // ============================================
  const Sidebar = () => (
    <aside className={`fixed left-0 top-0 h-full bg-[#1a237e] text-white transition-all duration-300 z-40 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
      {/* Logo */}
      <div className="p-4 border-b border-blue-800">
        <div className="flex items-center gap-3">
          <div className="bg-white text-[#1a237e] font-bold text-xl px-2 py-1 rounded flex-shrink-0">
            <span className="text-red-500">▶</span> AD
          </div>
          {sidebarOpen && (
            <div>
              <div className="font-bold">Admin Panel</div>
              <div className="text-xs text-blue-200">ImportBaba</div>
            </div>
          )}
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="p-4 space-y-2">
        {[
          { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
          { id: "orders", label: "Orders", icon: ShoppingCart },
          { id: "products", label: "Products", icon: Package },
          { id: "customers", label: "Customers", icon: Users },
          { id: "settings", label: "Store Settings", icon: Settings },
        ].map(item => (
          <button
            key={item.id}
            onClick={() => setCurrentPage(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              currentPage === item.id
                ? "bg-white/20 text-white"
                : "text-blue-200 hover:bg-white/10 hover:text-white"
            }`}
          >
            <item.icon size={20} />
            {sidebarOpen && <span>{item.label}</span>}
          </button>
        ))}
      </nav>
      
      {/* Logout */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-blue-800">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 text-blue-200 hover:bg-white/10 hover:text-white rounded-lg transition-colors"
        >
          <LogOut size={20} />
          {sidebarOpen && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );

  // ============================================
  // HEADER
  // ============================================
  const Header = () => (
    <header className={`fixed top-0 right-0 h-16 bg-white border-b z-30 transition-all duration-300 ${sidebarOpen ? 'left-64' : 'left-20'}`}>
      <div className="flex items-center justify-between h-full px-6">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <Menu size={20} />
        </button>
        
        <div className="flex items-center gap-4">
          <a 
            href="/" 
            target="_blank"
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Store size={18} />
            View Store
          </a>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1a237e] text-white rounded-full flex items-center justify-center font-bold">
              A
            </div>
            <span className="font-medium">{adminUser?.username}</span>
          </div>
        </div>
      </div>
    </header>
  );

  // ============================================
  // DASHBOARD PAGE
  // ============================================
  const DashboardPage = () => (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Orders</p>
              <p className="text-3xl font-bold text-gray-800">{stats.orders}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Products</p>
              <p className="text-3xl font-bold text-gray-800">{stats.products}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Package className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Customers</p>
              <p className="text-3xl font-bold text-gray-800">{stats.customers}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Revenue</p>
              <p className="text-3xl font-bold text-gray-800">${stats.revenue.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <DollarSign className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent Orders */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Recent Orders</h2>
          <button 
            onClick={() => setCurrentPage("orders")}
            className="text-blue-600 hover:underline text-sm"
          >
            View All
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-gray-500">Order ID</th>
                <th className="text-left p-4 text-sm font-medium text-gray-500">Customer</th>
                <th className="text-left p-4 text-sm font-medium text-gray-500">Date</th>
                <th className="text-left p-4 text-sm font-medium text-gray-500">Status</th>
                <th className="text-left p-4 text-sm font-medium text-gray-500">Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.length > 0 ? orders.slice(0, 5).map(order => (
                <tr key={order.id} className="border-t hover:bg-gray-50">
                  <td className="p-4 font-medium">{order.id}</td>
                  <td className="p-4">{order.customer?.name || order.customer?.email || "Guest"}</td>
                  <td className="p-4 text-gray-500">{order.date ? new Date(order.date).toLocaleDateString() : "-"}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      order.status === "delivered" ? "bg-green-100 text-green-700" :
                      order.status === "shipped" ? "bg-blue-100 text-blue-700" :
                      order.status === "processing" ? "bg-yellow-100 text-yellow-700" :
                      "bg-gray-100 text-gray-700"
                    }`}>
                      {order.status || "pending"}
                    </span>
                  </td>
                  <td className="p-4 font-semibold">${order.total?.toFixed(2)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-500">
                    No orders yet. Orders placed by customers will appear here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // ============================================
  // ORDERS PAGE
  // ============================================
  const OrdersPage = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Orders</h1>
        <button 
          onClick={loadData}
          className="px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] flex items-center gap-2"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      
      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Order ID</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Customer</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Items</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Date</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Status</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Total</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length > 0 ? orders.map(order => (
              <tr key={order.id} className="border-t hover:bg-gray-50">
                <td className="p-4 font-medium text-blue-600">{order.id}</td>
                <td className="p-4">
                  <div>
                    <p className="font-medium">{order.customer?.name || "Guest"}</p>
                    <p className="text-sm text-gray-500">{order.customer?.email}</p>
                  </div>
                </td>
                <td className="p-4">{order.items?.length || 0} items</td>
                <td className="p-4 text-gray-500">{new Date(order.date).toLocaleDateString()}</td>
                <td className="p-4">
                  <select
                    value={order.status || "pending"}
                    onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                    className={`px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer ${
                      order.status === "delivered" ? "bg-green-100 text-green-700" :
                      order.status === "shipped" ? "bg-blue-100 text-blue-700" :
                      order.status === "processing" ? "bg-yellow-100 text-yellow-700" :
                      order.status === "cancelled" ? "bg-red-100 text-red-700" :
                      "bg-gray-100 text-gray-700"
                    }`}
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
                <td className="p-4 font-semibold">${order.total?.toFixed(2)}</td>
                <td className="p-4">
                  <button 
                    onClick={() => setSelectedOrder(order)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Eye size={18} />
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="7" className="p-12 text-center">
                  <ShoppingCart size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">No orders yet</p>
                  <p className="text-gray-400 text-sm">When customers place orders, they will appear here</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold">Order {selectedOrder.id}</h2>
              <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Customer</p>
                  <p className="font-medium">{selectedOrder.customer?.name || "Guest"}</p>
                  <p className="text-sm text-gray-500">{selectedOrder.customer?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Order Date</p>
                  <p className="font-medium">{new Date(selectedOrder.date).toLocaleString()}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-2">Items</p>
                <div className="border rounded-lg overflow-hidden">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 border-b last:border-0">
                      <div className="w-12 h-12 bg-gray-100 rounded">
                        {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-contain" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-500">Qty: {item.quantity} × ${item.price?.toFixed(2)}</p>
                      </div>
                      <p className="font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-between items-center pt-4 border-t">
                <span className="text-lg font-semibold">Total</span>
                <span className="text-2xl font-bold text-[#1a237e]">${selectedOrder.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ============================================
  // PRODUCTS PAGE
  // ============================================
  const ProductsPage = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Products ({products.length})</h1>
        <button 
          onClick={syncProducts}
          disabled={loading}
          className="px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          Sync Products
        </button>
      </div>
      
      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search products by name or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      
      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {products
          .filter(p => !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku?.toLowerCase().includes(searchQuery.toLowerCase()))
          .slice(0, 50)
          .map(product => (
          <div key={product.sku} className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow">
            <div className="aspect-square bg-gray-100 rounded-lg mb-3 overflow-hidden">
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <Package size={40} />
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mb-1">{product.category}</p>
            <h3 className="font-medium text-sm line-clamp-2 mb-1">{product.name}</h3>
            <p className="text-xs text-gray-400 mb-2">SKU: {product.sku}</p>
            <div className="flex items-center justify-between">
              <span className="font-bold text-[#1a237e]">${product.price?.toFixed(2)}</span>
              <span className={`text-xs px-2 py-1 rounded ${
                product.stock_quantity > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}>
                {product.stock_quantity > 0 ? `${product.stock_quantity} in stock` : "Out of stock"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ============================================
  // CUSTOMERS PAGE
  // ============================================
  const CustomersPage = () => (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Customers</h1>
      
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Customer</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Email</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Orders</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500">Joined</th>
            </tr>
          </thead>
          <tbody>
            {customers.length > 0 ? customers.map((customer, idx) => (
              <tr key={idx} className="border-t hover:bg-gray-50">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center font-medium">
                      {customer.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <span className="font-medium">{customer.name || "Unknown"}</span>
                  </div>
                </td>
                <td className="p-4 text-gray-500">{customer.email}</td>
                <td className="p-4">{customer.orderCount || 0}</td>
                <td className="p-4 text-gray-500">{customer.joinedAt ? new Date(customer.joinedAt).toLocaleDateString() : "-"}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="4" className="p-12 text-center">
                  <Users size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">No customers yet</p>
                  <p className="text-gray-400 text-sm">When customers register, they will appear here</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============================================
  // SETTINGS PAGE
  // ============================================
  const saveStoreSettings = async () => {
    try {
      await axios.put(`${API}/store-settings`, storeSettings);
      toast.success("Settings saved!");
    } catch (err) {
      toast.error("Failed to save settings");
    }
  };

  const SettingsPage = () => (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Store Settings</h1>
      
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-4">General Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Store Name</label>
              <input
                type="text"
                value={storeSettings.store_name || ""}
                onChange={(e) => setStoreSettings({ ...storeSettings, store_name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="settings-store-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={storeSettings.email || ""}
                onChange={(e) => setStoreSettings({ ...storeSettings, email: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="settings-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                value={storeSettings.phone || ""}
                onChange={(e) => setStoreSettings({ ...storeSettings, phone: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="settings-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={storeSettings.address || ""}
                onChange={(e) => setStoreSettings({ ...storeSettings, address: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="settings-address"
              />
            </div>
          </div>
        </div>
        
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold mb-4">Warning Bars</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nicotine Warning (Black Bar)</label>
              <input
                type="text"
                value={storeSettings.nicotine_warning || ""}
                onChange={(e) => setStoreSettings({ ...storeSettings, nicotine_warning: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="settings-nicotine-warning"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Policy (Yellow Bar)</label>
              <input
                type="text"
                value={storeSettings.shipping_policy || ""}
                onChange={(e) => setStoreSettings({ ...storeSettings, shipping_policy: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="settings-shipping-policy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Final Notice (Red Bar)</label>
              <input
                type="text"
                value={storeSettings.sales_final_notice || ""}
                onChange={(e) => setStoreSettings({ ...storeSettings, sales_final_notice: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="settings-sales-notice"
              />
            </div>
          </div>
        </div>
        
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold mb-4">WhatsApp</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number</label>
              <input
                type="text"
                value={storeSettings.whatsapp_number || ""}
                onChange={(e) => setStoreSettings({ ...storeSettings, whatsapp_number: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Link</label>
              <input
                type="text"
                value={storeSettings.whatsapp_link || ""}
                onChange={(e) => setStoreSettings({ ...storeSettings, whatsapp_link: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
        
        <div className="pt-4">
          <button 
            onClick={saveStoreSettings}
            className="px-6 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] font-semibold"
            data-testid="save-settings-btn"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================
  // MAIN LAYOUT
  // ============================================
  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" />
      <Sidebar />
      <Header />
      
      {/* Main Content */}
      <main className={`pt-16 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <div className="p-6">
          {currentPage === "dashboard" && <DashboardPage />}
          {currentPage === "orders" && <OrdersPage />}
          {currentPage === "products" && <ProductsPage />}
          {currentPage === "customers" && <CustomersPage />}
          {currentPage === "settings" && <SettingsPage />}
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
